import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CentralContainer } from './components/CentralContainer/CentralContainer';
import { Col, Row } from 'antd';
import { setHeader } from '@app/store/slices/headerSlice';
import {
  CentralTrackingModel,
  CentralTrackingShippingModel,
} from '@app/domain/interfaces/centralTracking/CentralTrackingModel';
import { CardCentralTracking } from './components/CardCentralTracking/CardCentralTracking';
import { FooterHelper } from '@app/pages/modules/CentralTracking/components/FooterHelper/FooterHelper';
import { Spinner } from '@app/components/common/Spinner/Spinner';
import { motion } from 'framer-motion';
import ICentralTrackingService, { CentralTrackingService } from '@app/services/centralTrackingService';
import { notificationController } from '@app/controllers/notificationController';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import env from '@app/config/env.config';
import {
  CentralTrackingUpdateFinished,
  CentralTrackingUpdateSlump,
  CentralTrackingUpdateWater,
} from '@app/domain/interfaces/centralTracking/CentralTrackingUpdateModels';
import { PageTitle } from '@app/components/common/PageTitle/PageTitle';
import { HelperNotification } from '@app/components/common/HelperNotification/HelperNotification';
import { useSearchParams } from 'react-router-dom';
import { setHeaderBranchSelected, setHeaderGroupSelected } from '@app/store/slices/trackingSlice';
import ISignalRService, { SignalRService } from '@app/services/signalrService';

const transitionSpring = {
  type: 'spring',
  damping: 4,
  stiffness: 90,
};

const centralService: ICentralTrackingService = new CentralTrackingService();
const signalrService: ISignalRService = new SignalRService('');

const CentralTrackingDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [centralTrackingData, setCentralTrackingData] = React.useState<CentralTrackingModel>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(true);
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const { branchTrackingSelected, groupTrackingSelected, branchsTracking, groupsTracking } = useAppSelector(
    (state) => state.tracking,
  );
  const [waterReceived, setWaterReceived] = useState<CentralTrackingUpdateWater>();
  const [slumpReceived, setSlumpReceived] = useState<CentralTrackingUpdateSlump>();
  const [finishedReceived, setFinishedReceived] = useState<CentralTrackingUpdateFinished>();
  const [shippingReceived, setShippingReceived] = useState<CentralTrackingShippingModel>();
  const latestIdRef = useRef<number | null>(null);
  const centralTrackingRef = useRef<CentralTrackingModel>();

  const refreshData = async () => {
    let url = '/acompanhamento';
    if (branchTrackingSelected.codigoFilialSiac > 0) {
      url += `?grupo=${groupTrackingSelected?.id ?? 0}&filial=${branchTrackingSelected.codigoFilialSiac}`;
    }
    window.location.href = url;
  };

  const hidratarComDadosIniciais = (res: CentralTrackingModel) => {
    centralTrackingRef.current = res;

    const allRemessas = res.pontosCarga?.flatMap((p) => p.remessas ?? []) ?? [];

    const latestRemessa = allRemessas[0];
    if (latestRemessa) setShippingReceived(latestRemessa);
  };

  const fetch = useCallback(async (currentId?: number) => {
    if (!currentId) return;
    setLoading(true);
    centralService
      .getChargingPointsByCodigoFilial([currentId])
      .then((res) => {
        setLoading(false);
        setCentralTrackingData(res);
        hidratarComDadosIniciais(res);
      })
      .catch((err) => {
        setLoading(false);
        if (env.ENVIRONMENT === 'dev' || env.ENVIRONMENT === 'loc') {
          console.log(err);
        }
        setCentralTrackingData(undefined);
        notificationController.error({
          message: 'Erro! Não foi possível buscar os dados da central desta filial.',
        });
      });
  }, []);

  useEffect(() => {
    if (
      connection &&
      connection.state === HubConnectionState.Connected &&
      branchTrackingSelected.codigoFilialSiac > 0
    ) {
      latestIdRef.current = branchTrackingSelected.codigoFilialSiac;
      fetch(branchTrackingSelected.codigoFilialSiac);

      connection.on('acompanhamento_central_agua', (msg) => {
        if (msg.IdFilial == latestIdRef.current) {
          setWaterReceived({
            dataHora: msg.DataHora,
            idViagem: msg.IdViagem,
            aguaUtilizada: msg.AguaUtilizada,
            aguaUtilizadaPorcentagem: msg.AguaUtilizadaPorcentagem,
            rotacaoMotor: msg.RotacaoMotor,
            temperaturaAmbiente: msg.TemperaturaAmbiente,
            temperaturaConcreto: msg.TemperaturaConcreto,
          });
        }
      });
      connection.on('acompanhamento_central_slump', (msg) => {
        if (msg.IdFilial == latestIdRef.current) {
          setSlumpReceived({
            dataHora: msg.DataHora,
            idViagem: msg.IdViagem,
            rotacaoMotor: msg.RotacaoMotor,
            slump: msg.Slump,
          });
        }
      });
      connection.on('acompanhamento_central_finalizado', (msg) => {
        if (msg.IdFilial == latestIdRef.current) {
          setFinishedReceived({
            dataHora: msg.DataHora,
            idViagem: msg.IdViagem,
            finalizado: msg.Finalizado,
            finalizadoComErro: msg.FinalizadoComErro,
          });
        }
      });
      connection.on('acompanhamento_central_remessa', (msg) => {
        if (msg.IdFilial == latestIdRef.current) {
          setShippingReceived({
            dataHora: msg.DataHora,
            finalizado: msg.Finalizado,
            finalizadoComErro: msg.FinalizadoComErro,
            aguaHistorico: msg.AguaHistorico,
            aguaRetidaPorcentagem: msg.AguaRetidaPorcentagem,
            aguaTotal: msg.AguaTotal,
            aguaUtilizada: msg.AguaUtilizada,
            aguaUtilizadaPorcentagem: msg.AguaUtilizadaPorcentagem,
            betoneira: msg.Betoneira,
            cliente: msg.Cliente,
            dataEmissaoNf: msg.DataEmissaoNf,
            dataHoraFinalizado: msg.DataHoraFinalizado,
            dataHoraUltimaAtualizacaoAgua: msg.DataHoraUltimaAtualizacaoAgua,
            dataHoraUltimaAtualizacaoSlump: msg.DataHoraUltimaAtualizacaoSlump,
            deviceId: msg.DeviceId,
            idViagem: msg.IdViagem,
            motorista: msg.Motorista,
            motoristaMatricula: msg.MotoristaMatricula,
            obra: msg.Obra,
            obraId: msg.ObraId,
            pontoCarga: msg.PontoCarga,
            rotacaoMotor: msg.RotacaoMotor,
            slumpAlvo: msg.SlumpAlvo,
            slumpAtual: msg.SlumpAtual,
            slumpHistorico: msg.SlumpHistorico,
            slumpRange: msg.SlumpRange,
            temperaturaAmbiente: msg.TemperaturaAmbiente,
            temperaturaConcreto: msg.TemperaturaConcreto,
            tempoCentral: msg.TempoCentral,
            traco: msg.Traco,
            volume: msg.Volume,
          });
        }
      });

      connection
        .invoke('JoinGroup', `acompanhamento_central_${branchTrackingSelected.codigoFilialSiac}`)
        .catch((err) => {
          console.error('Erro ao entrar no grupo após troca de filial:', err);
        });

      return () => {
        connection
          .invoke('LeaveGroup', `acompanhamento_central_${branchTrackingSelected.codigoFilialSiac}`)
          .catch((err) => {
            console.error('Erro ao sair do grupo anterior:', err);
          });
      };
    }
  }, [branchTrackingSelected.codigoFilialSiac, connection, fetch]);

  useEffect(() => {
    centralTrackingRef.current = centralTrackingData;
  }, [centralTrackingData]);

  useEffect(() => {
    if (waterReceived && centralTrackingRef.current) {
      const prev = centralTrackingRef.current;
      const updated = {
        ...prev,
        pontosCarga: prev.pontosCarga.map((p) => ({
          ...p,
          remessas: p.remessas?.map((r) => {
            if (r.idViagem === waterReceived.idViagem) {
              const agua = { dataHora: waterReceived.dataHora, aguaUtilizada: waterReceived.aguaUtilizada };
              return {
                ...r,
                aguaHistorico: r.aguaHistorico ? [...r.aguaHistorico, agua] : [agua],
                aguaUtilizada: waterReceived.aguaUtilizada,
                aguaUtilizadaPorcentagem: waterReceived.aguaUtilizadaPorcentagem,
                dataHoraUltimaAtualizacaoAgua: waterReceived.dataHora,
                temperaturaAmbiente: waterReceived.temperaturaAmbiente,
                temperaturaConcreto: waterReceived.temperaturaConcreto,
                rotacaoMotor: waterReceived.rotacaoMotor,
              };
            } else return r;
          }),
        })),
      };
      setCentralTrackingData(updated);
    }
  }, [waterReceived]);

  useEffect(() => {
    if (slumpReceived && centralTrackingRef.current) {
      const prev = centralTrackingRef.current;
      const updated = {
        ...prev,
        pontosCarga: prev.pontosCarga.map((p) => ({
          ...p,
          remessas: p.remessas?.map((r) => {
            if (r.idViagem === slumpReceived.idViagem) {
              const slump = { dataHora: slumpReceived.dataHora, slump: slumpReceived.slump };
              return {
                ...r,
                slumpAtual: slumpReceived.slump,
                rotacaoMotor: slumpReceived.rotacaoMotor,
                dataHoraUltimaAtualizacaoSlump: slumpReceived.dataHora,
                slumpHistorico: r.slumpHistorico?.length ? [...r.slumpHistorico, slump] : [slump],
              };
            } else return r;
          }),
        })),
      };
      setCentralTrackingData(updated);
    }
  }, [slumpReceived]);

  useEffect(() => {
    if (finishedReceived && centralTrackingRef.current) {
      const prev = centralTrackingRef.current;
      const updated = {
        ...prev,
        pontosCarga: prev.pontosCarga.map((p) => ({
          ...p,
          remessas: p.remessas?.map((r) =>
            r.idViagem === finishedReceived.idViagem
              ? {
                  ...r,
                  finalizado: finishedReceived.finalizado ?? false,
                  finalizadoComErro: finishedReceived.finalizadoComErro ?? false,
                }
              : r,
          ),
        })),
      };
      setCentralTrackingData(updated);
    }
  }, [finishedReceived]);

  useEffect(() => {
    if (shippingReceived && centralTrackingRef.current) {
      const prev = centralTrackingRef.current;
      const hasPoint = prev.pontosCarga.find((p) => p.pontoCarga === shippingReceived.pontoCarga);
      const updated = hasPoint
        ? {
            ...prev,
            pontosCarga: prev.pontosCarga.map((p) =>
              p.pontoCarga === shippingReceived.pontoCarga
                ? { ...p, remessas: [...(p.remessas ?? []), shippingReceived] }
                : p,
            ),
          }
        : {
            ...prev,
            pontosCarga: [
              ...prev.pontosCarga,
              {
                idFilial: branchTrackingSelected.codigoFilialSiac,
                pontoCarga: shippingReceived.pontoCarga,
                remessas: [shippingReceived],
              },
            ],
          };
      setCentralTrackingData(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingReceived]);

  const handleConnectionToSignalR = async () => {
    setLoading(true);
    const response = await signalrService.getUrlAndAccessToken();

    if (!connection || connection.state === HubConnectionState.Disconnected) {
      const newConnection = new HubConnectionBuilder()
        .withUrl(response.url, {
          accessTokenFactory: () => response.accessToken,
          transport: HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Error)
        .build();

      newConnection
        .start()
        .then(() => {
          console.log('Connected to SignalR');
          setIsConnected(true);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Connection error: ', err);
          setIsConnected(false);
          setLoading(false);
        });

      newConnection.onreconnected(() => {
        setIsConnected(true);
        if (latestIdRef.current) {
          newConnection.invoke('JoinGroup', `acompanhamento_central_${latestIdRef.current}`).catch(console.error);
          fetch(latestIdRef.current);
        }
      });

      newConnection.onclose(() => setIsConnected(false));
      setConnection(newConnection);
    }
  };

  useEffect(() => {
    setLoading(true);
    dispatch(setHeader({ title: 'Acompanhamento da central' }));

    const grupo = searchParams.get('grupo');
    const filial = searchParams.get('filial');
    if (grupo && filial) {
      if (groupsTracking.length > 0 && branchsTracking.length > 0) {
        dispatch(setHeaderGroupSelected(groupsTracking.find((b) => b.id === grupo)));
        dispatch(setHeaderBranchSelected(branchsTracking.find((b) => b.codigoFilialSiac === Number(filial))));
      }
    }

    handleConnectionToSignalR();
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsTracking, branchsTracking]);

  return (
    <>
      <PageTitle>Acompanhamento central</PageTitle>
      <Spinner spinning={loading} />
      {!isConnected && (
        <HelperNotification iconColor="#FBBD43" style={{ background: '#FFF7E8', borderColor: '#FBBD43' }}>
          <span style={{ fontSize: '1.25rem' }}>
            O servidor de processamento em tempo real está desconectado.{' '}
            <span onClick={refreshData} style={{ textDecoration: 'underline', fontWeight: 'bold', cursor: 'pointer' }}>
              Clique aqui
            </span>{' '}
            para reconectar.
          </span>
        </HelperNotification>
      )}
      {branchTrackingSelected.codigoPontoDeCarga01 ? (
        <Row gutter={[15, 15]} style={{ width: '100%' }}>
          <Col span={24}>
            <CentralContainer
              title="P1"
              onClickReport={() =>
                window.open(
                  `/acompanhamento/relatorio/${branchTrackingSelected.codigoFilialSiac}/P1?regional=${groupTrackingSelected.nome}&filial=${branchTrackingSelected.nome}`,
                  '_blank',
                )
              }
            >
              {centralTrackingRef.current?.pontosCarga
                ?.filter((p) => p.pontoCarga == 'P1' && p.remessas?.find((r) => !r.finalizado))
                ?.map((point, index) => (
                  <Row key={index} gutter={[15, 15]} style={{ width: '100%' }}>
                    {point.remessas
                      ?.sort((a, b) => (a.finalizado == b.finalizado ? 0 : !a.finalizado ? -1 : 1))
                      ?.slice(0, 2)
                      ?.map((shipping) => {
                        return (
                          <Col key={`${shipping.idViagem}-${shipping.deviceId}`} xs={24} md={12}>
                            <motion.div
                              style={{ height: '100%' }}
                              className={`${
                                shipping.finalizado
                                  ? 'animate__animated animate__fadeOutRight'
                                  : 'animate__animated animate__fadeInLeft'
                              }`}
                              transition={transitionSpring}
                            >
                              <CardCentralTracking centralTrackingData={{ ...shipping }} onAnimatedEnd={() => {}} />
                            </motion.div>
                          </Col>
                        );
                      })}
                  </Row>
                ))}
            </CentralContainer>
          </Col>
        </Row>
      ) : (
        <></>
      )}
      {branchTrackingSelected.codigoPontoDeCarga02 ? (
        <Row gutter={[15, 15]} style={{ width: '100%' }}>
          <Col span={24}>
            <CentralContainer
              title="P2"
              onClickReport={() =>
                window.open(
                  `/acompanhamento/relatorio/${branchTrackingSelected.codigoFilialSiac}/P2?regional=${groupTrackingSelected.nome}&filial=${branchTrackingSelected.nome}`,
                  '_blank',
                )
              }
            >
              {centralTrackingRef.current?.pontosCarga
                ?.filter((p) => p.pontoCarga == 'P2' && p.remessas?.find((r) => !r.finalizado))
                ?.map((point, index) => (
                  <Row key={index} gutter={[15, 15]} style={{ width: '100%' }}>
                    {point.remessas
                      ?.sort((a, b) => (a.finalizado == b.finalizado ? 0 : !a.finalizado ? -1 : 1))
                      ?.slice(0, 2)
                      ?.map((shipping) => {
                        return (
                          <Col key={`${shipping.idViagem}-${shipping.deviceId}`} xs={24} md={12}>
                            <motion.div
                              style={{ height: '100%' }}
                              className={`${
                                shipping.finalizado
                                  ? 'animate__animated animate__fadeOutRight'
                                  : 'animate__animated animate__fadeInLeft'
                              }`}
                              transition={transitionSpring}
                            >
                              <CardCentralTracking centralTrackingData={{ ...shipping }} onAnimatedEnd={() => {}} />
                            </motion.div>
                          </Col>
                        );
                      })}
                  </Row>
                ))}
            </CentralContainer>
          </Col>
        </Row>
      ) : (
        <></>
      )}
      {branchTrackingSelected.codigoPontoDeCarga03 ? (
        <Row gutter={[15, 15]} style={{ width: '100%' }}>
          <Col span={24}>
            <CentralContainer
              title="P3"
              onClickReport={() =>
                window.open(
                  `/acompanhamento/relatorio/${branchTrackingSelected.codigoFilialSiac}/P3?regional=${groupTrackingSelected.nome}&filial=${branchTrackingSelected.nome}`,
                  '_blank',
                )
              }
            >
              {centralTrackingRef.current?.pontosCarga
                ?.filter((p) => p.pontoCarga == 'P3' && p.remessas?.find((r) => !r.finalizado))
                ?.map((point, index) => (
                  <Row key={index} gutter={[15, 15]} style={{ width: '100%' }}>
                    {point.remessas
                      ?.sort((a, b) => (a.finalizado == b.finalizado ? 0 : !a.finalizado ? -1 : 1))
                      ?.slice(0, 2)
                      ?.map((shipping) => (
                        <Col key={`${shipping.idViagem}-${shipping.deviceId}`} xs={24} md={12}>
                          <motion.div
                            style={{ height: '100%' }}
                            className={`${
                              shipping.finalizado
                                ? 'animate__animated animate__fadeOutRight'
                                : 'animate__animated animate__fadeInLeft'
                            }`}
                            transition={transitionSpring}
                          >
                            <CardCentralTracking centralTrackingData={{ ...shipping }} onAnimatedEnd={() => {}} />
                          </motion.div>
                        </Col>
                      ))}
                  </Row>
                ))}
            </CentralContainer>
          </Col>
        </Row>
      ) : (
        <></>
      )}

      <FooterHelper />
    </>
  );
};

export default CentralTrackingDashboard;
